import React, { memo } from "react";
import { shallowEqual } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RootState } from "typedefs";
import { useSelector } from "app/utils/hooks";
import Authenticated from "app/components/Wrappers/Authenticated";
import AcademyWizardGuard from "app/components/Layouts/AcademyWizardLayout/AcademyWizardGuard";
import ButtonVariant from "app/components/Button/ButtonVariant";
import coachnow_logo from "app/images/Logo_dark.svg";
import close_icon from "app/images/close-icon.svg";
// 720x513 (1x) webp with a real transparent background — Figma node 408:11763
// ("Group 47254"), exported from Figma with alpha intact.
import hero_image from "app/images/academy-wizard-hero.webp";
import { trackWizardStarted } from "app/utils/analytics-helpers/academySetup";
import {
  clearWizardSession,
  WIZARD_BACK_COUNT_KEY,
  WIZARD_EXIT_STATE,
  WIZARD_STARTED_AT_KEY,
} from "app/components/Layouts/AcademyWizardLayout";
import { academyWizardSteps } from "app/routes/AcademyWizard/steps";
import styles from "./styles.module.scss";

const AcademyWizardWelcome = () => {
  const { t } = useTranslation();
  const history = useHistory();
  // Set by the checkout flows that land here (trial_signup from /trial/academy,
  // upgrade from /purchase/academy); a direct visit has no state.
  const location = useLocation<{
    wizardEntryPoint?: "trial_signup" | "upgrade";
  }>();
  const { self } = useSelector((state: RootState) => state.user, shallowEqual);

  const firstName = self?.firstName || self?.displayName?.split(" ")[0] || "";

  const onBegin = () => {
    sessionStorage.setItem(WIZARD_STARTED_AT_KEY, String(Date.now()));
    sessionStorage.setItem(WIZARD_BACK_COUNT_KEY, "0");
    trackWizardStarted(location.state?.wizardEntryPoint || "other");
    history.push(academyWizardSteps[0].path);
  };

  return (
    <Authenticated>
      <AcademyWizardGuard>
        <div className={styles.page}>
          <header className={styles.top_bar}>
            <img src={coachnow_logo} alt="CoachNow" className={styles.logo} />
            <button
              type="button"
              className={styles.close_button}
              aria-label={t("Close")}
              onClick={() => {
                // A deliberate exit ends any session left by an earlier run.
                clearWizardSession();
                history.push("/academy/setup", WIZARD_EXIT_STATE);
              }}
            >
              <img src={close_icon} alt="" />
            </button>
          </header>
          <div className={styles.content_container}>
            <img src={hero_image} alt="" className={styles.hero} />
            <div className={styles.content}>
              <div className={styles.pill}>{t("Academy Setup")}</div>
              <h1 className={styles.title}>
                {firstName
                  ? t("Welcome to CoachNow Academy, {{firstName}}!", {
                      firstName,
                    })
                  : t("Welcome to CoachNow Academy!")}
              </h1>
              <p className={styles.text}>
                {t(
                  "Let's get started on your facility and coach profile. Once your basic information is set, we'll provide a step-by-step guide for setting up your calendar, payments, programs, and more.",
                )}
              </p>
            </div>
            <ButtonVariant
              title={t("Begin Setup")}
              buttonType="primary-filled"
              buttonStyles={styles.begin_button}
              onClickFunc={onBegin}
            />
          </div>
        </div>
      </AcademyWizardGuard>
    </Authenticated>
  );
};

export default memo(AcademyWizardWelcome);
